// Wizard Card Game Scoring Service
// Manages Wizard game state, bids, tricks, and scores
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Calculate total rounds based on number of players
// Wizard has 60 cards total, rounds = 60 / players
export const calculateTotalRounds = (playerCount) => {
  if (playerCount < 3 || playerCount > 6) {
    throw new Error('Wizard requires 3-6 players');
  }
  return Math.floor(60 / playerCount);
};

// Calculate score for a player's round
// Exact bid: +20 + (10 × tricks bid)
// Wrong bid: -10 × |difference between bid and tricks|
export const calculateRoundScore = (bid, tricks) => {
  if (bid === tricks) {
    return 20 + (10 * tricks);
  }
  return -10 * Math.abs(bid - tricks);
};

// Initialize a new Wizard game session
export const createWizardGame = async (gameId, players, drawnOrder) => {
  const wizardRef = doc(db, 'wizardGames', gameId);

  const playerCount = players.length;
  const totalRounds = calculateTotalRounds(playerCount);

  // Use drawnOrder for seating/dealer rotation, or default to players array
  const playerOrder = drawnOrder && drawnOrder.length === playerCount
    ? drawnOrder
    : players.map(p => p.id);

  // Initialize player data with names and starting scores
  const playerData = {};
  playerOrder.forEach((playerId, index) => {
    const player = players.find(p => p.id === playerId);
    playerData[playerId] = {
      name: player?.displayName || `Player ${index + 1}`,
      seatNumber: index + 1,
      totalScore: 0,
      rounds: {} // Will hold { roundNumber: { bid, tricks, score } }
    };
  });

  const wizardGame = {
    gameId,
    playerCount,
    totalRounds,
    currentRound: 1,
    playerOrder, // Array of player IDs in seat order
    playerData,
    status: 'bidding', // bidding, playing, round_complete, finished
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  await setDoc(wizardRef, wizardGame);

  return {
    id: gameId,
    ...wizardGame
  };
};

// Get Wizard game by ID
export const getWizardGame = async (gameId) => {
  const wizardRef = doc(db, 'wizardGames', gameId);
  const wizardSnap = await getDoc(wizardRef);

  if (!wizardSnap.exists()) {
    return null;
  }

  return {
    id: wizardSnap.id,
    ...wizardSnap.data()
  };
};

// Get dealer for a specific round (rotates through players)
// Round 1 = seat 1 deals, Round 2 = seat 2 deals, etc.
export const getDealerForRound = (playerOrder, roundNumber) => {
  const dealerIndex = (roundNumber - 1) % playerOrder.length;
  return {
    playerId: playerOrder[dealerIndex],
    seatNumber: dealerIndex + 1
  };
};

// Save bids for a round
export const saveBidsForRound = async (gameId, roundNumber, bids) => {
  const wizardGame = await getWizardGame(gameId);
  if (!wizardGame) throw new Error('Wizard game not found');

  const updatedPlayerData = { ...wizardGame.playerData };

  // bids is an object: { playerId: bidAmount }
  for (const [playerId, bid] of Object.entries(bids)) {
    if (!updatedPlayerData[playerId]) continue;

    if (!updatedPlayerData[playerId].rounds[roundNumber]) {
      updatedPlayerData[playerId].rounds[roundNumber] = {};
    }
    updatedPlayerData[playerId].rounds[roundNumber].bid = bid;
  }

  const wizardRef = doc(db, 'wizardGames', gameId);
  await updateDoc(wizardRef, {
    playerData: updatedPlayerData,
    status: 'playing',
    updatedAt: serverTimestamp()
  });
};

// Save tricks (results) for a round and calculate scores
export const saveTricksForRound = async (gameId, roundNumber, tricks) => {
  const wizardGame = await getWizardGame(gameId);
  if (!wizardGame) throw new Error('Wizard game not found');

  const updatedPlayerData = { ...wizardGame.playerData };

  // tricks is an object: { playerId: tricksWon }
  for (const [playerId, tricksWon] of Object.entries(tricks)) {
    if (!updatedPlayerData[playerId]) continue;

    const playerRound = updatedPlayerData[playerId].rounds[roundNumber];
    if (!playerRound || playerRound.bid === undefined) continue;

    const bid = playerRound.bid;
    const score = calculateRoundScore(bid, tricksWon);

    updatedPlayerData[playerId].rounds[roundNumber] = {
      ...playerRound,
      tricks: tricksWon,
      score
    };

    // Update total score
    updatedPlayerData[playerId].totalScore += score;
  }

  // Check if game is finished
  const isLastRound = roundNumber >= wizardGame.totalRounds;
  const newStatus = isLastRound ? 'finished' : 'round_complete';
  const newRound = isLastRound ? roundNumber : roundNumber;

  const wizardRef = doc(db, 'wizardGames', gameId);
  await updateDoc(wizardRef, {
    playerData: updatedPlayerData,
    status: newStatus,
    currentRound: newRound,
    updatedAt: serverTimestamp()
  });

  return {
    isLastRound,
    playerData: updatedPlayerData
  };
};

// Start next round
export const startNextRound = async (gameId) => {
  const wizardGame = await getWizardGame(gameId);
  if (!wizardGame) throw new Error('Wizard game not found');

  if (wizardGame.status === 'finished') {
    throw new Error('Game is already finished');
  }

  const nextRound = wizardGame.currentRound + 1;
  if (nextRound > wizardGame.totalRounds) {
    throw new Error('No more rounds');
  }

  const wizardRef = doc(db, 'wizardGames', gameId);
  await updateDoc(wizardRef, {
    currentRound: nextRound,
    status: 'bidding',
    updatedAt: serverTimestamp()
  });

  return nextRound;
};

// Get current standings (sorted by total score descending)
export const getStandings = (wizardGame) => {
  if (!wizardGame || !wizardGame.playerData) return [];

  return Object.entries(wizardGame.playerData)
    .map(([playerId, data]) => ({
      playerId,
      name: data.name,
      seatNumber: data.seatNumber,
      totalScore: data.totalScore,
      rounds: data.rounds
    }))
    .sort((a, b) => b.totalScore - a.totalScore);
};

// Get round summary for display
export const getRoundSummary = (wizardGame, roundNumber) => {
  if (!wizardGame || !wizardGame.playerData) return null;

  const summary = Object.entries(wizardGame.playerData)
    .map(([playerId, data]) => {
      const roundData = data.rounds[roundNumber] || {};
      return {
        playerId,
        name: data.name,
        seatNumber: data.seatNumber,
        bid: roundData.bid,
        tricks: roundData.tricks,
        score: roundData.score,
        totalScore: data.totalScore
      };
    })
    .sort((a, b) => a.seatNumber - b.seatNumber);

  return summary;
};

// Validate bids for a round
// In Wizard, the total bids cannot equal the number of cards in the round
// (to ensure at least one player will miss their bid)
export const validateBids = (roundNumber, bids, enforceRule = false) => {
  const totalBid = Object.values(bids).reduce((sum, bid) => sum + (bid || 0), 0);

  if (enforceRule && totalBid === roundNumber) {
    return {
      valid: false,
      message: `Total bids (${totalBid}) cannot equal the number of cards (${roundNumber})`
    };
  }

  return { valid: true };
};

// Validate tricks for a round
// Total tricks must equal number of cards in the round
export const validateTricks = (roundNumber, tricks) => {
  const totalTricks = Object.values(tricks).reduce((sum, t) => sum + (t || 0), 0);

  if (totalTricks !== roundNumber) {
    return {
      valid: false,
      message: `Total tricks (${totalTricks}) must equal number of cards (${roundNumber})`
    };
  }

  return { valid: true };
};

// Delete Wizard game
export const deleteWizardGame = async (gameId) => {
  const wizardRef = doc(db, 'wizardGames', gameId);
  await deleteDoc(wizardRef);
};

// Reset Wizard game to start fresh
export const resetWizardGame = async (gameId) => {
  const wizardGame = await getWizardGame(gameId);
  if (!wizardGame) throw new Error('Wizard game not found');

  // Reset player data
  const resetPlayerData = {};
  for (const [playerId, data] of Object.entries(wizardGame.playerData)) {
    resetPlayerData[playerId] = {
      ...data,
      totalScore: 0,
      rounds: {}
    };
  }

  const wizardRef = doc(db, 'wizardGames', gameId);
  await updateDoc(wizardRef, {
    playerData: resetPlayerData,
    currentRound: 1,
    status: 'bidding',
    updatedAt: serverTimestamp()
  });
};

// Edit a specific round's data (for corrections)
export const editRoundData = async (gameId, roundNumber, playerId, bid, tricks) => {
  const wizardGame = await getWizardGame(gameId);
  if (!wizardGame) throw new Error('Wizard game not found');

  const updatedPlayerData = { ...wizardGame.playerData };
  const playerData = updatedPlayerData[playerId];
  if (!playerData) throw new Error('Player not found');

  const oldRoundData = playerData.rounds[roundNumber] || {};
  const oldScore = oldRoundData.score || 0;

  // Calculate new score
  const newScore = calculateRoundScore(bid, tricks);

  // Update round data
  playerData.rounds[roundNumber] = {
    bid,
    tricks,
    score: newScore
  };

  // Recalculate total score
  playerData.totalScore = Object.values(playerData.rounds)
    .reduce((sum, r) => sum + (r.score || 0), 0);

  const wizardRef = doc(db, 'wizardGames', gameId);
  await updateDoc(wizardRef, {
    playerData: updatedPlayerData,
    updatedAt: serverTimestamp()
  });

  return {
    oldScore,
    newScore,
    newTotalScore: playerData.totalScore
  };
};
